import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { toast } from 'react-toastify'
import { assignExternalUser, removeExternalAssignee } from 'services/taskService'
import { useSeriesScope } from 'features/taskManagement/useSeriesScope'
import { hideEmail } from 'utils/user'

const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

const TaskExternalQuickEdit = ({ task, onSaved }) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const [list, setList] = useState(task?.externalAssignees || [])
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => { setList(task?.externalAssignees || []) }, [task?.externalAssignees])

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const width = 300
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12))
      setCoords({ top: Math.min(r.bottom + 6, window.innerHeight - 320), left })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const { promptNode, requestScope } = useSeriesScope(task)

  const invite = async () => {
    const e = email.trim()
    if (!isEmail(e)) { toast.info('Enter a valid email address.'); return }
    if (list.some(x => (x.email || '').toLowerCase() === e.toLowerCase())) { toast.info('That person is already invited.'); return }
    const scope = await requestScope()
    if (scope === null) return
    setBusy(true)
    try {
      const res = await assignExternalUser({ taskId: task.id, email: e, name: name.trim() || null, scope })
      const updated = res?.data?.data?.externalAssignees
      setList(Array.isArray(updated) ? updated : [...list, { id: e, email: e, name: name.trim() || null }])
      setEmail('')
      setName('')
      toast.success('Invite sent.')
      onSaved?.()
    } catch (err) {
      toast.error('Could not send the invite. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async assignee => {
    const scope = await requestScope()
    if (scope === null) return
    setBusy(true)
    try {
      const res = await removeExternalAssignee({ taskId: task.id, externalAssigneeId: assignee.id, scope })
      const updated = res?.data?.data?.externalAssignees
      setList(Array.isArray(updated) ? updated : list.filter(x => x.id !== assignee.id))
      toast.success('Removed.')
      onSaved?.()
    } catch (err) {
      toast.error('Could not remove that person. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!task?.id) return null

  const label = list.length === 0
    ? 'Invite'
    : list.length === 1
      ? (list[0].name || hideEmail(list[0].email))
      : `${list.length} external`

  return (
    <>
      <Trigger
        ref={btnRef}
        type='button'
        title='External users'
        $empty={list.length === 0}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
      >
        <MailOutlineIcon style={{ fontSize: 14 }} />
        <TriggerText>{label}</TriggerText>
      </Trigger>

      {open && coords && createPortal(
        <Pop ref={popRef} role='dialog' aria-label='External users' style={{ top: coords.top, left: coords.left }} onClick={e => e.stopPropagation()}>
          <PopLabel>External users</PopLabel>
          {list.length > 0 && (
            <Chips>
              {list.map(x => (
                <Chip key={x.id}>
                  <span>{x.name || hideEmail(x.email)}</span>
                  <RemoveBtn type='button' disabled={busy} onClick={() => remove(x)} aria-label='Remove'>
                    <CloseRoundedIcon style={{ fontSize: 13 }} />
                  </RemoveBtn>
                </Chip>
              ))}
            </Chips>
          )}
          <Input placeholder='Email address' value={email} autoFocus onChange={e => setEmail(e.target.value)} />
          <Input placeholder='Name (optional)' value={name} onChange={e => setName(e.target.value)} />
          <PopActions>
            <CancelBtn type='button' onClick={() => setOpen(false)}>Close</CancelBtn>
            <SaveBtn type='button' disabled={busy || !email.trim()} onClick={invite}>{busy ? 'Sending…' : 'Invite'}</SaveBtn>
          </PopActions>
        </Pop>,
        document.body
      )}
      {promptNode}
    </>
  )
}

export default TaskExternalQuickEdit

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px dashed ${({ $empty, theme }) => ($empty ? theme.colors.grey[500] : 'transparent')};
  background: ${({ $empty }) => ($empty ? '#fff' : '#f2f4f7')};
  color: ${({ $empty, theme }) => ($empty ? theme.colors.grey[800] : theme.colors.haiti)};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  &:hover { background: #e8f6f7; border-color: ${({ theme }) => theme.colors.secondary}; }
`
const TriggerText = styled.span`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const Pop = styled.div`
  position: fixed;
  z-index: 100010;
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
`
const PopLabel = styled.span`font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 800; color: ${({ theme }) => theme.colors.grey[800]};`
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 6px; max-height: 120px; overflow-y: auto;`
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 4px 3px 10px; border-radius: 999px;
  background: #f2f4f7; font-size: 12px; font-weight: 600; color: #33384a;
`
const RemoveBtn = styled.button`
  border: none; background: #e2e6ec; color: #5a6172;
  width: 18px; height: 18px; border-radius: 50%; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: #d3d8e0; }
  &:disabled { opacity: 0.5; cursor: default; }
`
const Input = styled.input`
  padding: 8px 11px; border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 9px; font: inherit; font-size: 13px; color: ${({ theme }) => theme.colors.haiti}; outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`
const PopActions = styled.div`display: flex; justify-content: flex-end; gap: 8px;`
const CancelBtn = styled.button`
  padding: 7px 13px; border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 999px; background: #fff; color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 12px; font-weight: 700; cursor: pointer;
`
const SaveBtn = styled.button`
  padding: 7px 15px; border: none; border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary}; color: #fff;
  font-size: 12px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`
